/** Access Medical Lab's general on-site collection guide, transcribed from
 *  Access's own instruction sheet. Shown with every on-site Access order. */

export interface AccessGuideStep {
  label?: string;
  text: string;
  substeps?: string[];
}

export interface AccessTube {
  name: string;
  /** Tube cap color. A fixed color, not a theme token: it is a clinical
   *  identifier that has to match the physical tube in both themes. */
  capColor: string;
  steps: AccessGuideStep[];
}

const CENTRIFUGE = "Centrifuge blood for 15 minutes at 3000–3400 rpm.";

export const ACCESS_FILL_NOTE = "Fill all tubes to the fill line.";

export const ACCESS_ORDER_OF_DRAW: AccessTube[] = [
  {
    name: "Light Blue (Citrate) tube with transfer tube",
    capColor: "#38bdf8",
    steps: [
      { text: "Invert tubes 8–10 times to mix the additives." },
      { text: CENTRIFUGE },
      { text: "Aliquot plasma into a transfer tube and freeze." },
      { text: "Write the sample type on the transfer tube." },
    ],
  },
  {
    name: "Serum Separator Tube (SST)",
    capColor: "#eab308",
    steps: [
      { text: "Slowly invert SST tubes 5–10 times." },
      {
        text: "Let the blood clot upright in a rack for no less than 30 minutes and no more than 60 minutes.",
      },
      { text: CENTRIFUGE },
    ],
  },
  {
    name: "Green (Lithium Heparin) tube",
    capColor: "#16a34a",
    steps: [{ text: "Invert the tube 10 times to dissolve the heparin." }],
  },
  {
    name: "Lavender (EDTA) tube",
    capColor: "#9333ea",
    steps: [{ text: "Invert Lavender tubes 8–10 times to mix the additives." }],
  },
  {
    name: "Royal Blue (EDTA) tube",
    capColor: "#1d4ed8",
    steps: [
      {
        label: "Whole blood",
        text: "Invert tubes 8–10 times to mix the additives.",
      },
      {
        label: "Plasma",
        text: "Invert tubes 8–10 times to mix the additives.",
        substeps: [
          CENTRIFUGE,
          "Separate the plasma from the tube and aliquot it into a transfer tube.",
          "Write the sample type on the transfer tube.",
        ],
      },
    ],
  },
  {
    name: "Gray tube",
    capColor: "#6b7280",
    steps: [{ text: "Invert Gray tubes 8–10 times to mix the additives." }],
  },
];

export const ACCESS_ADDITIONAL_TUBE_HANDLING: AccessTube[] = [
  {
    name: "Serum Separator Tube (SST) with amber transfer tube",
    capColor: "#eab308",
    steps: [
      { text: "Foil-wrap the SST before drawing the patient (do not remove)." },
      { text: "Let the blood clot for 45 minutes." },
      { text: CENTRIFUGE },
      { text: "Aliquot serum into an amber transfer tube and freeze." },
      { text: "Write the sample type on the transfer tube." },
    ],
  },
  {
    name: "Lavender (EDTA) tube with amber transfer tube",
    capColor: "#9333ea",
    steps: [
      {
        text: "Foil-wrap the Lavender tube before drawing the patient (do not remove).",
      },
      { text: CENTRIFUGE },
      { text: "Aliquot plasma into an amber transfer tube and freeze." },
      { text: "Write the sample type on the transfer tube." },
    ],
  },
];

export const ACCESS_LABELING: AccessGuideStep[] = [
  { text: "Check the patient's name against a legal photo ID." },
  {
    text: "Label every tube with the patient's:",
    substeps: ["First and last name", "Date of birth"],
  },
  {
    text: "Write the date of collection on every tube, including transfer tubes.",
  },
];

export const ACCESS_SHIPPING: AccessGuideStep[] = [
  {
    label: "Prepare the specimen for transport",
    text: "",
    substeps: [
      "Place all tubes into a biohazard bag.",
      "Put the lab requisition form in the outer pocket of the biohazard specimen bag.",
      "Put the specimen bag into a foil bubble bag with an ice pack.",
      "Place the foil bubble bag into the Access Transport Box.",
      "Put the Access Transport Box inside a UPS Lab Pack.",
    ],
  },
  {
    label: "Ship the specimen",
    text: "",
    substeps: [
      "Drop it off at a UPS Store, or schedule a pickup before the Next Day Air cutoff time.",
      "Ship Monday to Friday only — no weekend shipments.",
    ],
  },
];
